import * as models from "../../lib/api/models";
import CalendarInput from "../CalendarInput";
import Modal from "../Modal";
import RadioGroup from "../RadioGroup";
import React, {useState} from "react";
import Select from "../Select";
import styles from "./FundLaterReminderModal.module.css";

const strings = {
    fundLater: "Fund Later Reminder",
    remindMe: "Remind Me",
    description1: "We can remind up to 14 days from now to make your deposit",
    description2: "Pick a date and amount that work for you",
    date: "Pick a date",
    deposit: "Deposit Amount",
    text: "Text notification at",
    textExtraInfo:
        "By providing your phone number, you consent to our use of automatic dialers, text or pre-recorded or artificial voice messages to service your account. Message and data rates may apply.",
    email: "Email notification at",
    fieldRequired: "This field is required",
};

const PHONE_VALUE = 1;
const EMAIL_VALUE = 2;

enum Fields {
    RemindDate = "remindDate",
    DepositAmount = "depositAmount",
}

interface Focusable {
    focus: () => void;
}

type Props<T> = {
    isOpen: boolean;
    onClose: () => void;
    onSubmit: (remindDate: Date, depositIndex: number, type: models.SecuredReminderType) => void;
    triggerElement: T | null;
    email: string;
    lastFourPhoneDigits: string;
    depositOptions: string[];
};

export default function FundLaterReminderModal<T extends Focusable>(props: Props<T>) {
    const [remindDate, setRemindDate] = useState<Date | null>(null);
    const [depositIndex, setDepositIndex] = useState(-1);
    const [notificationPref, setNotificationPref] = useState(1);
    const [validation, setValidation] = useState<{[key: string]: string[] | undefined}>({
        [Fields.RemindDate]: undefined,
        [Fields.DepositAmount]: undefined,
    });

    function onSubmit() {
        if (remindDate !== null && depositIndex >= 0) {
            props.onSubmit(
                remindDate,
                depositIndex,
                notificationPref === PHONE_VALUE
                    ? models.SecuredReminderType.SMS
                    : models.SecuredReminderType.Email,
            );
        } else {
            setValidation({
                [Fields.RemindDate]: remindDate === null ? [strings.fieldRequired] : undefined,
                [Fields.DepositAmount]: depositIndex < 0 ? [strings.fieldRequired] : undefined,
            });
        }
    }

    function setDate(d: Date) {
        setRemindDate(d);
        if (validation[Fields.RemindDate]) {
            setValidation((prev) => ({...prev, [Fields.RemindDate]: undefined}));
        }
    }

    function setDeposit(n: number) {
        setDepositIndex(n);
        if (validation[Fields.DepositAmount]) {
            setValidation((prev) => ({...prev, [Fields.DepositAmount]: undefined}));
        }
    }

    return (
        <Modal
            id="fund-later-reminder"
            isOpen={props.isOpen}
            label={strings.fundLater}
            onClose={props.onClose}
            triggerElement={props.triggerElement}
            onSubmit={onSubmit}
            onSubmitLabel={strings.remindMe}
        >
            <div className={styles.description}>
                <div>{strings.description1}</div>
                <div>{strings.description2}</div>
            </div>
            <CalendarInput
                id="fund-later-reminder-date"
                label={strings.date}
                value={remindDate}
                setValue={setDate}
                required={true}
                validationMessage={validation[Fields.RemindDate]}
            />
            <Select
                className={styles.deposit}
                id="fund-later-deposit-amount"
                label={strings.deposit}
                options={props.depositOptions}
                selectedIndex={depositIndex}
                setSelected={setDeposit}
                required={true}
                validationMessage={validation[Fields.DepositAmount]}
            />
            <RadioGroup
                radioInputs={[
                    {
                        value: PHONE_VALUE,
                        label: `${strings.text} XXX-XXX-${props.lastFourPhoneDigits}`,
                        extraInfo: strings.textExtraInfo,
                    },
                    {value: EMAIL_VALUE, label: `${strings.email} ${props.email}`},
                ]}
                selectedValue={notificationPref}
                setValue={(n) => setNotificationPref(n)}
                isVertical={true}
            />
        </Modal>
    );
}
